<template lang="html">
<div class="annual-runoff-chart">
    <svg id="flow-duration-annual-runoff" :width="svgWidth" :height="svgHeight">
        <g class="chart" :transform="`translate(${margin.left},${margin.top})`"></g>
    </svg>
    <chart-tooltip
    v-if="tooltipVisible"
    :top="tooltipPosition[1]"
    :left="tooltipPosition[0]"
    :content="tooltipText"
    />
</div>
</template>

<script>
import * as d3 from 'd3';
import numeral from 'numeral';

import ChartTooltip from '@/components/charts/ChartTooltip';
import sciNotation from '@/functions/sciNotationFmt';

export default {
    components: {
        ChartTooltip,
    },
    props: {
        // filtered data from crossfilter group.all()
        data: {
            type: Array,
            required: true,
        },
        // all (unfiltered) data (for preserving scales/ranges)
        dataAll: {
            type: Array,
            required: true,
        },
        // dimension filters from crossfilter dimension.currentFilter()
        dimensionFilter: {
            required: true,
        },
    },
    data() {
        return {
            // @TEMP: make these into props
            svgHeight: window.innerHeight - 275,
            svgWidth: window.innerWidth / 3.34,
            svg: null,
            g: null,
            margin: {
                top: 40,
                bottom: 35,
                left: 50,
                right: 50,
            },
            height: 270,
            width: 500,
            xScaleLog: null,
            yScaleTime: null,
            barHeight: 11, // this gets changed later on but we can use this initial value
            axisTop: null,
            initialized: false,
            // tooltip props
            tooltipPosition: [0, 0],
            tooltipVisible: false,
            tooltipText: [],
        };
    },
    computed: {
        chartData() {
            return this.formatData(this.data);
        },
        chartDataAll() {
            return this.formatData(this.dataAll);
        },
    },
    mounted() {
        this.initializeSvg();
    },
    methods: {
        /**
         * initialize svg chart element and define variables
         */
        initializeSvg() {
            // initialize chart & vars
            this.svg = d3.select('#flow-duration-annual-runoff');
            this.g = this.svg.select('g.chart');
            // chart (not element) size
            this.width = +this.svg.attr('width') - this.margin.left - this.margin.right;
            // calculate the height of this chart with respect to the number of datapoints in the chart
            // makes the calculation of the barheight below render with uniform values.
            // 1px for padding
            this.height = d3.max([(this.chartDataAll.length * (this.barHeight + 1)), 200]);
            this.svgHeight = this.height + this.margin.top;
            // this.height = +this.svg.attr('height') - this.margin.top - this.margin.bottom;
            // initialize scales
            this.xScaleLog = d3.scaleLog().rangeRound([0, this.width]).clamp(true);
            this.yScaleTime = d3.scaleTime().rangeRound([0, this.height]);
            // transition for bar size and axis
            this.transition = d3.transition().duration(500);
            // set y-scale input domain
            const maxDate = d3.max(this.chartDataAll, d => d.date);
            this.yScaleTime.domain([
                d3.min(this.chartDataAll, d => d.date),
                d3.timeDay.offset(d3.timeYear.offset(maxDate), -1), // end of year
            ]);

            // top axis
            this.axisTop = d3.axisTop(this.xScaleLog)
            .tickSize(6)
            .ticks(2)
            .tickFormat(sciNotation);
            this.g.append('g')
            .attr('class', 'axis axis--x')
            .attr('transform', 'translate(0,-3)');

            // left axis
            this.g.append('g')
            .attr('class', 'axis axis--y')
            .attr('transform', 'translate(-2,0)')
            .call(d3.axisLeft(this.yScaleTime)
            .ticks(this.chartDataAll.length, '%Y'));

            // add brush
            this.brush = d3.brushY()
            .extent([[0, 0], [this.width, this.height]])
            .on('end', this.brushEnded);

            // define & update bars
            this.updateChart();

            // axis label
            this.g.append('text')
            .attr('class', 'axis-label')
            .attr('transform', 'translate(3,3) rotate(-90)')
            .attr('y', 6)
            .attr('dy', '0.71em')
            .attr('text-anchor', 'end')
            .text('Runoff (m³)');

            // add selection brush
            this.g.append('g')
            .attr('class', 'brush annual-runoff')
            .call(this.brush);

            // track mouse events on the chart area
            this.g.on('mousemove', this.mouseMove);
            this.g.on('mouseout', this.mouseOut);

            this.initialized = true;
        },
        /**
         * format input data with d3 date objects and adjusted values
         * fills in missing years with null values
         * @param  {Array} input input data from Crossfilter
         * @return {Array}       adjusted data with year, date, & adjusted value
         */
        formatData(input) {
            if (!input.length) {
                return [];
            }
            // fill in any missing years with null values
            const denseArray = [];
            const firstYear = input[0].key;
            const lastYear = input[input.length - 1].key;
            const yearMap = new Map();
            input.forEach((e) => {
                yearMap.set(e.key, e);
            });
            for (let i = firstYear; i < lastYear; i += 1) {
                const thisYear = yearMap.get(i);
                if (thisYear) {
                    denseArray.push(thisYear);
                } else {
                    denseArray.push({
                        key: i,
                        value: null,
                    });
                }
            }

            // parse dates and adjust values
            const formatted = denseArray.map((d) => {
                const year = d.key;
                const date = d3.timeParse('%Y')(year);
                const value = (d.value === null) ? null : d.value * 86400; // convert to m3/yr
                return {
                    date,
                    value,
                };
            });

            return formatted;
        },
        /**
         * update chart bars & axis when data changes
         */
        updateChart() {
            // adjust scales: update x-scale input domain
            const minXVal = d3.min(this.chartData, d => d.value);
            this.xScaleLog.domain([
                d3.max([1, minXVal]),
                d3.max(this.chartData, d => d.value),
            ]).nice();

            // update x-axis
            this.g.select('.axis--x')
            .transition(this.transition)
            .call(this.axisTop)
            .selectAll('text')
            .attr('transform', 'translate(13,-10) rotate(-45)');

            // d3 "data join"
            const bars = this.g.selectAll('.bar')
            .data(this.chartData);

            // enter selection: create new elements
            bars.enter().append('rect')
            .attr('class', 'bar')
            .attr('x', 0)
            .attr('y', d => this.yScaleTime(d.date))
            .attr('height', this.barHeight)
            // update selection: resize bars
            .merge(bars)
            .transition(this.transition)
            .attr('width', d => this.xScaleLog(d.value) || 0);
        },
        /**
         * callback for d3 brush end selection events
         * calculates the year range of the selection
         */
        brushEnded() {
            if (!d3.event.sourceEvent) return; // Only transition after input.
            // clear filters for empty selection
            if (!d3.event.selection) {
                this.clearFilters();
                return;
            }

            const s = d3.event.selection;
            const d0 = s.map(this.yScaleTime.invert);
            const d1 = d0.map(d3.timeYear.round);

            // If empty when rounded, use floor & ceil instead.
            if (d1[0] >= d1[1]) {
                d1[0] = d3.timeYear.floor(d0[0]);
                d1[1] = d3.timeYear.offset(d1[0]);
            }

            d3.select('.brush.annual-runoff')
            .transition()
            .call(d3.event.target.move, d1.map(this.yScaleTime));

            this.updateFilters(d1);
        },
        /**
         * emit an event with a new filter range
         * @param  {Array} dates array with min and max years
         */
        updateFilters(dates) {
            const years = dates.map(y => +d3.timeFormat('%Y')(y));
            // "filterRange does not include the top point"
            years[1] += 1; // adjust for crossfilter behavior
            this.$emit('updateFilters', years);
        },
        /**
         * emit null to clear dimension filters
         */
        clearFilters() {
            this.$emit('updateFilters', null);
        },
        /**
         * move brush to reflect changed filters
         * pass null to clear brush
         * @param  {Array|null} years params for group.filter method
         */
        setBrush(selection) {
            // clear brush on null (no transition)
            if (selection === null) {
                d3.select('.brush.annual-runoff')
                .call(this.brush.move, null);
                return;
            }
            // convert years integer to pixel values
            const selectionRange = selection.map((y) => {
                const date = new Date(y, 1, 1);
                return this.yScaleTime(date);
            });
            // move brush
            d3.select('.brush.annual-runoff')
            .transition(this.transition)
            .call(this.brush.move, selectionRange);
        },
        /**
         * update tooltip when the mouse moves
         */
        mouseMove() {
            const [gX, gY] = d3.mouse(this.g.node());
            // hide on mouseout
            if (gX < 0 || gX > this.width || gY < 0 || gY > this.height) {
                this.mouseOut();
                return;
            }

            // position relative to the root flow duration tool element
            this.tooltipPosition = d3.mouse(this.$parent.$el);
            const date = this.yScaleTime.invert(gY);
            const yearString = d3.timeFormat('%Y')(date);
            const bisect = d3.bisector(d => d.date).left;
            const i = bisect(this.chartData, d3.timeYear.floor(date));
            const yearData = this.chartData[i];
            if (!yearData) {
                return;
            }
            let { value } = yearData;
            value = numeral(value).format('0,0');

            this.tooltipText = [
                { text: yearString, class: 'headline' },
                { text: 'Discharge', class: 'subheading' },
                { text: `${value} m³`, class: 'blue darken-2 white--text' },
            ];
            this.tooltipVisible = true;
        },
        /**
         * hide the tooltip on mouseout
         */
        mouseOut() {
            this.tooltipVisible = false;
        },
    },
    watch: {
        // update chart when data changes
        data: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
        // update brush selection when cf filter changes externally
        dimensionFilter(selection) {
            this.setBrush(selection);
        },
    },
};
</script>

<style lang="scss">
.annual-runoff-chart {

    rect.bar {
        fill: #1976D2;
        shape-rendering: crispEdges;
    }
    g.axis {
        user-select: none;
        .tick > text {
            font-size: 0.9rem;
        }
    }
    .overlay {
        pointer-events: all;
    }

    .axis-label {
        shape-rendering: optimizeLegibility;
        user-select: none;
        font-weight: 500;
        fill: #000;
        text-shadow: 0 0 1px #fff,
            -1px 0 1px #fff,
            1px 0 1px #fff;
    }

    // brush styles
    .brush {
        .selection {
            fill: rgba(#81D4FA, .5);
            fill-opacity: 1;
            stroke: none;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/charts/FDAnnualRunoff.vue